import type {
  PackagingContainer,
  CreatePackagingContainerRequest,
  AssignPackagingRequest,
  PalletType,
  CreatePalletTypeRequest,
  LoadingPlanResponse,
  GenerateLoadingPlanRequest,
  ConvertToPhysicalRequest,
  BoxPosition,
  PalletLayout,
} from "../types/loading-plan";
import { getAuthHeaders, fetchWithAuth, handleResponse } from "./api";

const API_BASE_URL =
  (window as any).ENV?.VITE_API_URL ||
  import.meta.env?.VITE_API_URL ||
  "http://localhost:5000";

// Packaging Containers API
export const packagingContainersApi = {
  async getAll(): Promise<PackagingContainer[]> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/packaging-containers`,
      {
        headers: getAuthHeaders(),
      },
    );
    return handleResponse<PackagingContainer[]>(response);
  },

  async getById(id: string): Promise<PackagingContainer> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/packaging-containers/${id}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return handleResponse<PackagingContainer>(response);
  },

  async create(
    data: CreatePackagingContainerRequest,
  ): Promise<PackagingContainer> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/packaging-containers`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      },
    );
    return handleResponse<PackagingContainer>(response);
  },

  async update(
    id: string,
    data: Omit<CreatePackagingContainerRequest, "code">,
  ): Promise<PackagingContainer> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/packaging-containers/${id}`,
      {
        method: "PUT",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      },
    );
    return handleResponse<PackagingContainer>(response);
  },

  async delete(id: string): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/packaging-containers/${id}`,
      {
        method: "DELETE",
        headers: getAuthHeaders(),
      },
    );
    if (!response.ok && response.status !== 204) {
      throw new Error(
        `Failed to delete packaging container: ${response.statusText}`,
      );
    }
  },

  async assignToProduct(
    containerId: string,
    productId: string,
    data: AssignPackagingRequest,
  ): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/packaging-containers/${containerId}/assign-to-product/${productId}`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) {
      throw new Error(`Failed to assign packaging: ${response.statusText}`);
    }
  },

  async setDefaultForProduct(
    containerId: string,
    productId: string,
  ): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/packaging-containers/${containerId}/set-default-for-product/${productId}`,
      {
        method: "PUT",
        headers: getAuthHeaders(),
      },
    );
    if (!response.ok) {
      throw new Error(
        `Failed to set default packaging: ${response.statusText}`,
      );
    }
  },
};

// Pallet Types API
export const palletTypesApi = {
  async getAll(): Promise<PalletType[]> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/loading-plans/pallet-types`,
      {
        headers: getAuthHeaders(),
      },
    );
    return handleResponse<PalletType[]>(response);
  },

  async create(data: CreatePalletTypeRequest): Promise<PalletType> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/loading-plans/pallet-types`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      },
    );
    return handleResponse<PalletType>(response);
  },
};

// Loading Plans API
export const loadingPlansApi = {
  async getAll(filters?: {
    routeId?: string;
    startDate?: string;
    endDate?: string;
    status?: string;
  }): Promise<LoadingPlanResponse[]> {
    const params = new URLSearchParams();
    if (filters?.routeId) params.append("routeId", filters.routeId);
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    if (filters?.status) params.append("status", filters.status);

    const url = `${API_BASE_URL}/api/loading-plans${params.toString() ? "?" + params.toString() : ""}`;
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse<LoadingPlanResponse[]>(response);
  },

  async generate(
    data: GenerateLoadingPlanRequest,
  ): Promise<LoadingPlanResponse> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/loading-plans/generate`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      },
    );
    return handleResponse<LoadingPlanResponse>(response);
  },

  async getById(id: string): Promise<LoadingPlanResponse> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/loading-plans/${id}`,
      {
        headers: getAuthHeaders(),
      },
    );
    return handleResponse<LoadingPlanResponse>(response);
  },

  async getByRoute(
    routeId: string,
    date?: string,
  ): Promise<LoadingPlanResponse[]> {
    const params = new URLSearchParams();
    if (date) params.append("date", date);

    const url = `${API_BASE_URL}/api/loading-plans/by-route/${routeId}${params.toString() ? "?" + params.toString() : ""}`;
    const response = await fetchWithAuth(url, {
      headers: getAuthHeaders(),
    });
    return handleResponse<LoadingPlanResponse[]>(response);
  },

  async getByOrder(orderId: string): Promise<LoadingPlanResponse | null> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/loading-plans/by-order/${orderId}`,
      {
        headers: getAuthHeaders(),
      },
    );
    const data = await handleResponse<LoadingPlanResponse | null>(response);
    return data;
  },

  async recalculate(
    id: string,
    orderGroups?: string[][],
    groupAlgorithms?: (string | null)[],
  ): Promise<LoadingPlanResponse> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/loading-plans/${id}/recalculate`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify({ orderGroups, groupAlgorithms }),
      },
    );
    return handleResponse<LoadingPlanResponse>(response);
  },

  async approve(id: string): Promise<LoadingPlanResponse> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/loading-plans/${id}/approve`,
      {
        method: "POST",
        headers: getAuthHeaders(),
      },
    );
    return handleResponse<LoadingPlanResponse>(response);
  },

  async complete(id: string): Promise<LoadingPlanResponse> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/loading-plans/${id}/complete`,
      {
        method: "POST",
        headers: getAuthHeaders(),
      },
    );
    return handleResponse<LoadingPlanResponse>(response);
  },

  async convertVirtualToPhysical(
    virtualPalletId: string,
    data: ConvertToPhysicalRequest,
  ): Promise<void> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/loading-plans/virtual-pallets/${virtualPalletId}/convert-to-physical`,
      {
        method: "POST",
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      },
    );
    if (!response.ok) {
      throw new Error(
        `Failed to convert virtual pallet: ${response.statusText}`,
      );
    }
  },

  async getVirtualPalletLayout(id: string): Promise<PalletLayout> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/loading-plans/virtual-pallets/${id}/3d-layout`,
      {
        headers: getAuthHeaders(),
      },
    );
    return handleResponse<PalletLayout>(response);
  },

  async getPhysicalPalletLayout(id: string): Promise<PalletLayout> {
    const response = await fetchWithAuth(
      `${API_BASE_URL}/api/loading-plans/physical-pallets/${id}/3d-layout`,
      {
        headers: getAuthHeaders(),
      },
    );
    return handleResponse<PalletLayout>(response);
  },
};
